import { styled } from "styled-components";
import { useNavigate } from "react-router-dom";
import { usePastAuctions } from "../hooks/useAuctions";
import { useTestimonials } from "../hooks/useTestimonials";
import ProductCard from "../components/common/ProductCard";
import { Spinner } from "../components/Spinner";
import TestimonialSection from "../components/common/TestimonialSection";
import PartnersContainer from "../components/common/PartnersContainer";
import { MenuItems } from "../contexts";
import {
  generateWinnerAvatar,
  determineCategory,
  formatPrice,
} from "../lib/mockWinnerData";
import { getAuctionSavingsLabel } from "../lib/auctionPresentation";

// Page wrapper
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
`;

// Main section with orange gradient background
const WinnersSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 60px 60px;
  width: 100%;
  background: ${({ theme }) => theme.gradients.orange};
  box-sizing: border-box;
  min-height: 100vh;

  @media (max-width: 767px) {
    padding: 30px 20px 50px;
  }
`;

// Content container with max-width
const WinnersContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  max-width: 1608px;
  margin: 0 auto;
`;

// Page title
const PageTitle = styled.h1`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 600;
  font-size: 40px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.white};
  margin: 0;

  @media (max-width: 767px) {
    font-size: 32px;
  }
`;

// Grid for winner cards
const WinnersGrid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;
  gap: 25px;
  width: 100%;
  flex-wrap: wrap;
`;

// Loading container
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 400px;
`;

// Error message
const ErrorMessage = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  padding: 40px 20px;
`;

// Empty state
const EmptyState = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  padding: 60px 20px;
  width: 100%;
`;

const Winners = () => {
  const { data: pastAuctions, isLoading, isError } = usePastAuctions();
  const { data: testimonials = [] } = useTestimonials();
  const navigate = useNavigate();

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper>
        <WinnersSection>
          <WinnersContent>
            <PageTitle>Proběhlé aukce</PageTitle>
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          </WinnersContent>
        </WinnersSection>
        <TestimonialSection testimonials={testimonials} />
        <PartnersContainer />
      </PageWrapper>
    );
  }

  // Error state
  if (isError) {
    return (
      <PageWrapper>
        <WinnersSection>
          <WinnersContent>
            <PageTitle>Proběhlé aukce</PageTitle>
            <ErrorMessage>
              Nepodařilo se načíst proběhlé aukce. Zkuste to prosím znovu později.
            </ErrorMessage>
          </WinnersContent>
        </WinnersSection>
        <TestimonialSection testimonials={testimonials} />
        <PartnersContainer />
      </PageWrapper>
    );
  }

  // Empty state
  if (!pastAuctions || pastAuctions.length === 0) {
    return (
      <PageWrapper>
        <WinnersSection>
          <WinnersContent>
            <PageTitle>Proběhlé aukce</PageTitle>
            <EmptyState>
              Zatím neproběhly žádné aukce. Buďte první, kdo vyhraje!
            </EmptyState>
          </WinnersContent>
        </WinnersSection>
        <TestimonialSection testimonials={testimonials} />
        <PartnersContainer />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <WinnersSection>
        <WinnersContent>
          <PageTitle>Proběhlé aukce</PageTitle>
          <WinnersGrid>
            {pastAuctions
              .filter((auction) => {
                // Filter out auctions with missing critical data
                return auction.title && auction.photoUrls && auction.photoUrls.length > 0;
              })
              .map((auction, index) => {
                // Generate mock winner data
                const avatarUrl = generateWinnerAvatar(auction.auctionId || index);
                const savings =
                  getAuctionSavingsLabel(auction.price, auction.estimatedRetailPrice) ||
                  "Výhodná výhra";
                const category = determineCategory(auction.title);
                const soldPrice = formatPrice(auction.price);
                const imageUrl = auction.photoUrls[0];

                return (
                  <ProductCard
                    key={auction.auctionId || index}
                    productName={auction.title}
                    productLabel={category}
                    avatarUrl={avatarUrl}
                    imageUrl={imageUrl}
                    soldPrice={soldPrice}
                    savings={savings}
                    onClick={() =>
                      navigate(`/${MenuItems.Winners}/${auction.auctionId}`, {
                        state: {
                          auction,
                          source: MenuItems.Winners,
                        },
                      })
                    }
                  />
                );
              })}
          </WinnersGrid>
        </WinnersContent>
      </WinnersSection>
      <TestimonialSection testimonials={testimonials} />
      <PartnersContainer />
    </PageWrapper>
  );
};

export default Winners;
